import { useCallback } from 'react'

import moment from 'moment'
import { groupBy, map } from 'lodash'

import { Alert, Fade } from '@mui/material'
import { AssignmentTurnedInOutlined, ContentPasteOffOutlined } from '@mui/icons-material'

import { useDispatch, useSelector } from '@tabeeb/store'
import { TenantPermission } from '@tabeeb/enums'
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Group,
  OverflowInlineList,
  Stack,
  ConfirmationDialog,
  useDialogState,
} from '@collaborate-health/uikit'
import { hasTenantPermission } from '@tabeeb/modules/permissions/selectors'
import { CertificateStatusType } from '@tabeeb/contracts/microservices/swagger-certificate-service'
import InfoRow from '@tabeeb/modules/qualifications/components/ViewUserQualificationDialog/InfoRow'
import { onAddErrorNotification } from '@tabeeb/modules/notification/actions'

import { getReviewAllowed } from '../../helpers'
import { useCertificate } from '../../hooks'
import { Certificate } from '../../types'

import CertificateSkeleton from './CertificateSkeleton'
import CertificatePreview from '../CertificatePreview'
import CertificateStatusChip from '../CertificateStatusChip'
import CertificateOwner from '../CertificateOwner'
import RejectCertificateDialog from '../RejectCertificateDialog'

export interface ViewCertificateDialogProps {
  open: boolean
  onClose: () => void
  onExited?: () => void
  certificate?: Certificate | null
  onReview?: (payload: {
    id: number
    statusId: CertificateStatusType.Approved | CertificateStatusType.Rejected
    rejectionReason?: string | null
  }) => void
  personal: boolean
}

const ViewCertificateDialog: React.FC<ViewCertificateDialogProps> = ({
  open,
  personal,
  certificate: certificateBase,
  onReview,
  onClose,
  onExited,
}) => {
  const dispatch = useDispatch()

  const hasManageCertificatesTenantScopePermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.CertificatesTenantScope)
  )
  const hasManageCertificatesGlobalScopePermission = useSelector((state) =>
    hasTenantPermission(state, TenantPermission.CertificatesGlobalScope)
  )

  const onError = useCallback(() => {
    dispatch(onAddErrorNotification({ message: 'Failed to load certificate details' }))
    onClose()
  }, [dispatch, onClose])

  const { certificate, loading } = useCertificate({
    id: certificateBase?.Id!,
    enabled: open && Boolean(certificateBase),
    subdomain: certificateBase?.TenantSubdomain,
    onError,
  })

  const [approveDialogOpen, onOpenApprovalConfirmationDialog, onCloseApprovalConfirmationDialog] = useDialogState()
  const [rejectDialogOpen, onOpenRejectionConfirmationDialog, onCloseRejectionConfirmationDialog] = useDialogState()

  const handleConfirmApprove = useCallback(() => {
    onReview?.({ id: certificate?.Id!, statusId: CertificateStatusType.Approved })
    onCloseApprovalConfirmationDialog()
    onClose()
  }, [onReview, certificate?.Id, onCloseApprovalConfirmationDialog, onClose])

  const handleConfirmReject = useCallback(
    (reason?: string | null) => {
      onCloseRejectionConfirmationDialog()
      onReview?.({ id: certificate?.Id!, statusId: CertificateStatusType.Rejected, rejectionReason: reason })
      onClose()
    },
    [certificate?.Id, onCloseRejectionConfirmationDialog, onReview, onClose]
  )

  const reviewAllowed = getReviewAllowed({
    personal,
    certificate,
    hasManageCertificatesTenantScopePermission,
    hasManageCertificatesGlobalScopePermission,
  })

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        TransitionProps={{
          onExited: () => {
            onCloseApprovalConfirmationDialog()
            onCloseRejectionConfirmationDialog()

            onExited?.()
          },
        }}
        fullWidth
        maxWidth='md'
      >
        <DialogTitle>View certificate</DialogTitle>
        <DialogContent>
          {!certificate || loading ? (
            <CertificateSkeleton />
          ) : (
            <Grid container spacing={2}>
              <Grid item md={6} xs={12} height={450}>
                <CertificatePreview src={certificate.PictureUrl} />
              </Grid>
              <Grid item md={6} xs={12}>
                <Stack gap={2}>
                  <Group fullWidth>
                    <Stack gap={2}>
                      {certificate.SerialNumber && <InfoRow label='Serial number'>{certificate.SerialNumber}</InfoRow>}
                      {certificate.Authority && <InfoRow label='Authority'>{certificate.Authority}</InfoRow>}
                      <InfoRow label='Category'>{certificate.CategoryName}</InfoRow>
                      <InfoRow label='Type'>{certificate.TypeName}</InfoRow>
                      <InfoRow label='Status'>
                        <CertificateStatusChip status={certificate.StatusId} />
                      </InfoRow>
                      <InfoRow label='Owner'>
                        <Stack maxWidth={200} overflow='hidden'>
                          <CertificateOwner certificate={certificate} />
                        </Stack>
                      </InfoRow>
                      <InfoRow label='Created on'>
                        {moment(certificate.CreatedOn, 'YYYY-MM-DDTHH:mm:ss.SSSSSS').format('MM-DD-YYYY hh:mm A')}
                      </InfoRow>
                      <InfoRow label='Valid from'>
                        {new Date(certificate.ValidFrom).toLocaleDateString('en-US')}
                      </InfoRow>
                      <InfoRow label='Valid till'>
                        {new Date(certificate.ExpirationDate).toLocaleDateString('en-US')}
                      </InfoRow>
                    </Stack>
                  </Group>

                  {certificate.Locations.length > 0 &&
                    map(
                      groupBy(certificate.Locations, (location) => location.CountryName),
                      (locations, countryName) => (
                        <Group key={countryName} title={`Locations: ${countryName}`}>
                          <OverflowInlineList
                            items={locations}
                            direction='row'
                            moreCountPosition='separate'
                            getItemKey={(l) => l.Id}
                            hiddenListContainerProps={{
                              maxWidth: 200,
                            }}
                            renderItem={(l) => (
                              <Chip key={l.Id} color='primary' size='small' label={l.Name} maxWidth={150} />
                            )}
                            renderHiddenItem={(l) => <Chip key={l.Id} color='primary' size='small' label={l.Name} />}
                          />
                        </Group>
                      )
                    )}

                  <Fade in={Boolean(certificate.RejectionReason)} unmountOnExit>
                    <Alert severity='error' title='Rejection reason' variant='outlined'>
                      {certificate.RejectionReason}
                    </Alert>
                  </Fade>
                </Stack>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Stack direction='row' flex={1} mr={1}>
            <Button color='primary' onClick={onClose}>
              Close
            </Button>
          </Stack>
          {open && !loading && reviewAllowed && (
            <>
              <Button
                startIcon={<ContentPasteOffOutlined />}
                variant='outlined'
                color='error'
                onClick={onOpenRejectionConfirmationDialog}
              >
                Reject
              </Button>

              <Button
                startIcon={<AssignmentTurnedInOutlined />}
                variant='outlined'
                color='success'
                onClick={onOpenApprovalConfirmationDialog}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        isOpen={approveDialogOpen}
        title='Are you sure you want to approve the certificate?'
        submitButtonText='Approve'
        cancelButtonText='Cancel'
        onCloseDialog={onCloseApprovalConfirmationDialog}
        onSubmit={handleConfirmApprove}
      />
      <RejectCertificateDialog
        open={rejectDialogOpen}
        onClose={onCloseRejectionConfirmationDialog}
        onSubmit={handleConfirmReject}
      />
    </>
  )
}

export default ViewCertificateDialog

